import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config';
import { ManualLedger } from '../../types';
import { generateManualLedgerKey } from './keys';

// Manual Ledgers Collection
export const addManualLedger = async (ledger: Omit<ManualLedger, 'id' | 'createdAt'>): Promise<string> => {
  try {
    // Create custom key: companyName-sixDigits
    const customKey = generateManualLedgerKey(ledger.companyName);
    
    // Check if manual ledger already exists with this key
    const existingLedger = await getManualLedger(customKey);
    if (existingLedger) {
      // If key exists, generate a new one
      const newKey = generateManualLedgerKey(ledger.companyName);
      const docRef = doc(db, 'manualLedgers', newKey);
      await setDoc(docRef, {
        ...ledger,
        createdAt: serverTimestamp()
      });
      return newKey;
    }
    
    const docRef = doc(db, 'manualLedgers', customKey);
    await setDoc(docRef, {
      ...ledger,
      createdAt: serverTimestamp()
    });
    return customKey;
  } catch (error) {
    throw new Error(`Error adding manual ledger: ${error}`);
  }
};

export const getManualLedgers = async (): Promise<ManualLedger[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'manualLedgers'));
    const result = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        entries: data.entries?.map((entry: any) => ({
          ...entry,
          date: entry.date?.toDate() || new Date(entry.date)
        })) || []
      };
    }) as ManualLedger[];
    
    return result;
  } catch (error) {
    throw new Error(`Error getting manual ledgers: ${error}`);
  }
};

export const getManualLedger = async (id: string): Promise<ManualLedger | null> => {
  try {
    const docRef = doc(db, 'manualLedgers', id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      entries: data.entries?.map((entry: any) => ({
        ...entry,
        date: entry.date?.toDate() || new Date(entry.date)
      })) || []
    } as ManualLedger;
  } catch (error) {
    throw new Error(`Error getting manual ledger: ${error}`);
  }
};

export const updateManualLedger = async (id: string, ledger: Omit<ManualLedger, 'id' | 'createdAt'>): Promise<void> => {
  try {
    const docRef = doc(db, 'manualLedgers', id);
    await updateDoc(docRef, {
      ...ledger
    });
  } catch (error) {
    throw new Error(`Error updating manual ledger: ${error}`);
  }
};

export const deleteManualLedger = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, 'manualLedgers', id);
    await deleteDoc(docRef);
  } catch (error) {
    throw new Error(`Error deleting manual ledger: ${error}`);
  }
};
